import { Portal } from './../../shared-components/portal.model';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Classroom360Service } from '../../classroom360/classroom360.service';
import { detailExpand } from '../../shared-components/angular-material/mat-animations';
import { MenuItem } from '../../shared-components/controls/menus/menu-item.model';
import { CourseDescriptionDialogComponent } from '../../shared-components/course-description-dialog/course-description-dialog.component';
import { ExportService } from '../../shared-components/export.service';
import { SharedService } from '../../shared-components/shared.service';
import { StudentService } from '../../shared-components/student/student.service';
import { System } from '../../shared-components/System';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { SendEmailService } from '../../shared-modules/send-email/send-email.service';
import { Student360Service } from '../student360.service';
import { TylerMatTableDataSource } from './../../shared-components/angular-material/extended.class';
import { ProfileViewerDialogComponent } from './../../shared-components/profile-viewer-dialog/profile-viewer-dialog.component';
import { CourseSchedule } from './../../shared-components/student/course-schedule.model';
import { Enrollment } from './../../shared-components/student/enrollment.model';
import { AssignmentService } from './../assignment/assignment.service';
import { StudentScheduleService } from './student-schedule.service';

@Component({
    selector: 'student-schedule',
    templateUrl: './student-schedule.component.html',
    styleUrls: [
        './student-schedule.component.css'
    ],
    animations: [
        detailExpand
    ]
})
export class StudentScheduleComponent implements OnInit, AfterViewInit, OnDestroy {

    // Properties
    private _viewOption = 1;
    private _includeDropped = false;
    schedule: CourseSchedule[];
    subscriptions: Subscription[] = [];
    tableData = new TylerMatTableDataSource();
    junkDate = System.junkDate;
    V10: boolean;
    columns = ['More', 'Meets', 'Term', 'Course', 'Teacher', 'Room', 'EntryDate', 'DroppedDate', 'Team'];
    selectedRow: CourseSchedule;
    expandEmitter = new EventEmitter<boolean>();
    tableHeight: number;
    minTableWidth: number;
    @ViewChild('tableContainer', { read: ElementRef }) tableContainer: ElementRef;
    showProfiles: boolean;
    studentEnrollment: Enrollment;
    _sort: MatSort;
    clearFilterEvent = new EventEmitter<any>();
    parentPortalSubscription: Subscription;
    staffAssignedPortalsSubscription: Subscription;
    student360YearSubscription: Subscription;
    // ------------Selection/Flags-----------------------------------
    showAttendanceLink = false;
    showAssignmentLink = false;
    showGradesLink = false;
    showEmailLink = false;
    calendarUseAttendanceTypes = false;
    // Class Functions
    constructor(
        private studentScheduleService: StudentScheduleService,
        private loginService: LoginService,
        private router: Router,
        private dialog: MatDialog,
        private studentService: StudentService,
        private sendEmailService: SendEmailService,
        private assignmentService: AssignmentService,
        private sharedService: SharedService,
        private exportService: ExportService,
        public changeDetector: ChangeDetectorRef,
        private activatedRoute: ActivatedRoute,
        private userService: UserService,
        private parentPortalService: Student360Service,
        private classroom360Service: Classroom360Service,
        private yearService: YearService
    ) {
    }

    get viewOption(): number {
        return this._viewOption;
    }

    set viewOption(value: number) {
        this._viewOption = value;
        this.getSchedule();
    }

    get includeDropped(): boolean {
        return this._includeDropped;
    }

    set includeDropped(value: boolean) {
        this.tableData.data = [];
        this.schedule = undefined;
        this._includeDropped = value;
        this.getSchedule();
        setTimeout(() => {
            this.sharedService.updateShowSpinner(true);
            setTimeout(() => {
                this.checkColumns();
                this.sharedService.updateShowSpinner(false);
            }, 25);
        }, 25);
    }

    get sort(): MatSort {
        return this._sort;
    }

    @ViewChild(MatSort) set sort(value: MatSort) {
        this._sort = value;
        if (this.tableData) {
            this.tableData.sort = this._sort;
        }
    }

    // Event Functions
    ngOnInit() {
        this.activatedRoute.queryParams.subscribe(params => {
            const selectedView = params['selectedView'];
            // 1 = Today's schedule
            // 2 = This term
            // 3 = All
            if (selectedView) {
                this._viewOption = parseInt(selectedView, 10);
            }
        });
        // POST: initializes the data
        this.V10 = this.loginService.LoginSettings.V10;
        this.showProfiles = this.loginService.LoginSettings.ParentPortalCourseScheduleProfiles;
        this.checkColumns();
        this.getCalendarUseAttendanceTypes();

        this.subscriptions.push(
            this.expandEmitter.subscribe(expand => {
                this.tableData.expandAll(expand);
            }),
            this.studentService.selectedStudentStream$.subscribe(() => {
                this.schedule = undefined;
                this.studentEnrollment = this.studentService.studentEnrollment;
                this.getCalendarUseAttendanceTypes();
                this.getSchedule();
            }),
            this.yearService.observableStudent360YearStream$.subscribe(student360Year => {
                this.getCalendarUseAttendanceTypes();
                this.getSchedule();
            })
        );

        if (this.userService.userType == 1) {
            this.staffAssignedPortalsSubscription = this.classroom360Service.observableStaffAssignedPortalsStream$.subscribe(_ => {
                this.getPortalInfo();
            });
        }
        else {
            this.parentPortalSubscription = this.parentPortalService.observableParentAssignedPortalsStream$.subscribe(_ => {
                this.getPortalInfo();
            });
        }
        this.getPortalInfo();
    }

    ngAfterViewInit() {
        // POST: sets the sort to the table data
        if (this.tableData && this.sort) {
            this.tableData.sort = this.sort;
        }
    }

    ngOnDestroy() {
        // POST: unsubscribes to all observables
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    getPortalInfo() {
        const hasPortal = (portalName: string, excludeClassroomPortals: boolean) => {
            let portals: Portal[] = [];
            if (this.userService.userType == 1) {
                portals = this.classroom360Service.staffAssignedPortals;
            } else {
                portals = this.parentPortalService.parentAssignedPortals;
            }
            return portals.filter(x => x.PortalName == portalName && x.Disabled == false && (!excludeClassroomPortals || x.ParentModule != '/classroom360')) != null
                    && portals.filter(x => x.PortalName == portalName && x.Disabled == false && (!excludeClassroomPortals || x.ParentModule != '/classroom360')).length > 0;
        };
        this.showAttendanceLink = hasPortal('Attendance', true);
        this.showAssignmentLink = hasPortal('Assignments', true);
        this.showGradesLink = hasPortal('Grades', true);
        this.showEmailLink = hasPortal('Send Email', false);
    }

    assignmentClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on an assignment link under a course
        // POST: routes the user to that assignment page with this as the selected course
        this.assignmentService.selectedCourse = scheduleCourse;
        this.assignmentService.preventTabSwitch = true;
        const allowableTerms = scheduleCourse.CourseTerms.filter(term => {
            const today = Date.now();
            if (term.EndDate && term.StartDate) {
                const startDate = Date.parse(term.StartDate);
                const endDate = Date.parse(term.EndDate);
                if (today >= startDate && today <= endDate) {
                    return true;
                }
            }
            return false;
        });
        if (allowableTerms.length > 0) {
            this.assignmentService.selectedGP = allowableTerms[allowableTerms.length - 1].ShortDescription;
        }
        this.assignmentService.selectedSectionId = scheduleCourse.SiteSectionId;
        if (!this.userService.studentInformationView) {
            this.assignmentService.selectedView = this.viewOption;
            this.router.navigateByUrl('/student360/assignments');
        }
        else {
            this.userService.updateStudentViewRoute('/student360/assignments');
        }
    }

    attendanceClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on an attendance link under a course
        // POST: routes the user to that attendance page with by student schedule (all) as selected
        this.studentService.selectedCourse = scheduleCourse;
        this.studentService.selectedSectionId = scheduleCourse.SiteSectionId;
        this.studentService.selected = '3';
        if (!this.userService.studentInformationView) {
            this.router.navigateByUrl('/student360/attendance');
        }
        else {
            this.userService.updateStudentViewRoute('/student360/attendance');
        }
    }

    emailTeacherClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on an attendance link under a course
        // POST: routes the user to the email page
        this.sendEmailService.teacherName = scheduleCourse.TeacherName;
        this.sendEmailService.teacherEmailAddress = scheduleCourse.TeacherEmail;
        if (!this.userService.studentInformationView) {
            this.router.navigateByUrl('/student360/sendEmail');
        }
        else {
            this.userService.updateStudentViewRoute('/student360/sendEmail');
        }
    }

    gradesClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on a grade link under a course
        // POST: routes the user to the grade page
        if (!this.userService.studentInformationView) {
            this.router.navigateByUrl('/student360/reportCardGrades');
        }
        else {
            this.userService.updateStudentViewRoute('/student360/reportCardGrades');
        }
    }

    menuClick(item: MenuItem) {
        // PRE: a menu item is clicked
        // POST: routes to the right handler
        switch (item.itemName) {
            case 'Course Description':
                this.courseDescriptionClick(item.data);
                break;
            case 'Class Information':
                this.classInformationClick(item.data);
                break;
            case 'Teacher Profile':
                this.teacherProfileClick(item.data);
                break;
            default:
                break;
        }
    }

    courseDescriptionClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on a course description link under a course
        // POST: shows a modal for the course's description
        this.dialog.open(CourseDescriptionDialogComponent, {
            data: {
                course: scheduleCourse.Course,
                courseSection: scheduleCourse.SubjectCode + '-' + scheduleCourse.Section,
                teacherName: scheduleCourse.TeacherName,
                schoolName: scheduleCourse.SchoolName,
                curriculum: scheduleCourse.Curriculum,
                description: scheduleCourse.Description
            },
            disableClose: true
        });
    }

    classInformationClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on a class information link under a course
        // POST: shows a modal for that class' profile
        const dialogBox = this.dialog.open(ProfileViewerDialogComponent, {
            data: {
                courseSSEC_ID: scheduleCourse.Id,
                courseName: scheduleCourse.Course,
                courseSection: scheduleCourse.SubjectCode + '-' + scheduleCourse.Section,
                teacherName: scheduleCourse.TeacherName,
                school: scheduleCourse.SchoolName,
                profiles: scheduleCourse.CourseProfile,
                showTeacherProfileLink: scheduleCourse.TeacherProfile && scheduleCourse.TeacherProfile.length > 0
            },
            disableClose: true
        });
        this.subscriptions.push(
            dialogBox.componentInstance.teacherProfileClick.subscribe(() => {
                this.teacherProfileClick(scheduleCourse);
            })
        );
    }

    teacherProfileClick(scheduleCourse: CourseSchedule) {
        // PRE: the user clicks on a teacher profile link under a couse
        // POST: shows a modal for that teacher's profile
        this.dialog.open(ProfileViewerDialogComponent, {
            data: {
                teacherId: scheduleCourse.TeacherId,
                teacherName: scheduleCourse.TeacherName,
                school: scheduleCourse.SchoolName,
                profiles: scheduleCourse.TeacherProfile
            },
            disableClose: true
        });
    }

    getSchedule() {
        // POST: obtains the schedule from the server
        this.subscriptions.push(
            this.studentScheduleService.getStudentSchedule(this.viewOption, false, false, false, this.includeDropped, true, false, false).subscribe(schedule => {
                this.schedule = schedule;
                this.setGridHeader();
                for (let i = 0; i < this.schedule.length; i++) {
                    this.schedule[i] = System.convert<CourseSchedule>(this.schedule[i], new CourseSchedule());
                }
                this.tableData.data = this.schedule;
                if (this.sort) {
                    this.tableData.sort = this.sort;
                }
            })
        );
    }

    getMenuItems(scheduleCourse): string[] {
        // POST: returns the menu items for a given schedule course
        // Rearranged to be in alphabetical order
        const menuItems = [];
        menuItems.push('Class Information');
        menuItems.push('Course Description');
        menuItems.push('Teacher Profile');
        return menuItems;
    }

    checkColumns() {
        // POST: checks the columns for ones that shouldn't be there

        // Team is a V9 only column
        if (this.V10 && this.columns.includes('Team')) {
            this.columns.splice(this.columns.indexOf('Team'), 1);
        } else if (!this.V10 && !this.columns.includes('Team')) {
            this.columns.push('Team');  // Team is always on the end
        }

        // Entry date and DroppedDate are only there if include dropped
        if (this.includeDropped) {
            if (!this.columns.includes('EntryDate')) {
                this.columns.splice(6, 0, 'EntryDate');
            }
            if (!this.columns.includes('DroppedDate')) {
                this.columns.splice(7, 0, 'DroppedDate');
            }
            this.minTableWidth = 1000;
        } else {
            if (this.columns.includes('DroppedDate')) {
                this.columns.splice(this.columns.indexOf('DroppedDate'), 1);
            }
            if (this.columns.includes('EntryDate')) {
                this.columns.splice(this.columns.indexOf('EntryDate'), 1);
            }
            this.minTableWidth = 750;
        }
    }

    export() {
        this.schedule.forEach(schedule => {
            schedule['CourseSection'] = schedule.SubjectCode + '-' + schedule.Section;
        });

        this.exportService.exportMatGridToExcel(this.schedule, this.columns, 'studentSchedule', ['CourseSection'], ['More']);
    }

    getCalendarUseAttendanceTypes() {
        if (!this.V10) {
            this.sharedService.getCalendarUseAttendanceTypes(0, this.yearService.student360Year.Id, this.studentService.selectedStudent.Id).subscribe(result => {
                this.calendarUseAttendanceTypes = result;
                this.setGridHeader();
            });
        }
    }

    setGridHeader() {
        if (!this.V10) {
            if (this.calendarUseAttendanceTypes && this.viewOption == 1) {
                this.columns = ['More', 'Meets', 'AttendanceType', 'Term', 'Course', 'Teacher', 'Room', 'EntryDate', 'DroppedDate', 'Team'];
            }
            else {
                this.columns = ['More', 'Meets', 'Term', 'Course', 'Teacher', 'Room', 'EntryDate', 'DroppedDate', 'Team'];
            }
        }
    }
}
